/*
 * Navigation guards for the router. Each guard decides whether a route may open,
 * or sends the user somewhere else.
 */
import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router';
import { useAuthStore } from 'stores/auth';
import { useApplicationStore } from 'stores/application';
import { useServerStore } from 'stores/server';
import { useCookieLoginStore } from 'stores/cookieLogin';
import { isCookieAuthMode, reloadPage } from 'src/boot/authMode';

export const ifNotAuthenticated = (
  _to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
) => {
  const authStore = useAuthStore();
  // If user is NOT authenticated, let them go to requested not-protected page
  if (
    !authStore.isAuthenticated ||
    authStore.requiresSecondFactorAuth ||
    authStore.requiresEmployeeCell ||
    authStore.requiresPasswordChange ||
    authStore.requiresAgreement ||
    authStore.requiresQuickStart
  ) {
    next();
    return;
  }
  // If user is authenticated, send them to the authenticated home page
  if (from.path !== '/home') {
    // redirecting to home
    next('/home');
  } else {
    // Cancel the navigation so the user stays on /home. Without a call, it hangs.
    next(false);
  }
};

export const checkGeofence = (
  _to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext,
) => {
  const applicationStore = useApplicationStore();
  const serverStore = useServerStore();
  const geofenceInfo = serverStore.geofenceInfo;
  if (!geofenceInfo) {
    next();
    return;
  }

  if (!geofenceInfo.enabled) {
    next();
    return;
  }

  if (!applicationStore.clientCountry) {
    next();
    return;
  }

  const country = applicationStore.clientCountry;
  if (geofenceInfo.blockedCountries.includes(country)) {
    next({ name: 'blocked', query: { reason: 'Geofence' } });
    return;
  }
  if (
    geofenceInfo.allowedCountries &&
    geofenceInfo.allowedCountries.length > 0 &&
    !geofenceInfo.allowedCountries.includes(country)
  ) {
    next({ name: 'blocked', query: { reason: 'Geofence' } });
    return;
  }

  next();
};

export const ifRfsAuthenticated = (
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext,
) => {
  const authStore = useAuthStore();
  // User has the rsfFormSubmitter role
  if (authStore.hasGlobalRole('rfsFormSubmitter')) {
    next();
  } else {
    // User does not have the role, send them to login page
    authStore.redirect = to.fullPath;
    next('/');
  }
};

export const ifAuthenticated = (
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext,
) => {
  const authStore = useAuthStore();

  // If the user is a referral doctor, he cannot access any part of the app except the referral case
  if (authStore.isReferralDoctor) {
    authStore.authLogout().then(() => {
      next('/referral/notallowed');
    });
    // next is called once, above. Falling through would also call next('/')
    // and save a login redirect, sending the doctor to login instead.
    return;
  }
  // If user is partially authenticated, redirect to required page
  if (authStore.requiresEmployeeCell) {
    // redirecting to get the user's cell phone number
    next('/login/updatephone');
    return;
  } else if (authStore.requiresSecondFactorAuth) {
    // redirecting to second factor
    next('/login/second');
    return;
  } else if (authStore.isAuthenticated) {
    if (authStore.requiresPasswordChange) {
      // redirecting to password change
      next('/login/change');
    } else if (authStore.requiresAgreement) {
      // redirecting to terms agreement
      next('/login/agree');
    } else if (authStore.requiresQuickStart && !authStore.isDoctor) {
      // redirecting to profile info confirmation page
      next('/login/quickstart');
    } else if (authStore.hasGlobalRole('rfsFormSubmitter')) {
      next('/parks/dashboard');
    } else {
      // Authentication complete, let them go where they want to
      if (authStore.redirect && authStore.redirect.length > 0) {
        const to = authStore.redirect;
        authStore.redirect = '';
        next(to);
        return;
      }
      next();
    }
    return;
  }
  // User is not authenticated, send them to log in page
  authStore.redirect = to.fullPath;
  next('/');
};

/*
 * Cookie mode's MFA, MFA setup and reset screens need the challenge a Go login handed
 * out. It is held in memory only, so after a reload it is gone: start over.
 */
export const ifPendingMfa = (
  _to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext,
) => {
  if (useCookieLoginStore().pendingMFAChallenge) {
    next();
  } else {
    next({ name: 'login' });
  }
};

export const ifPendingMfaSetup = (
  _to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext,
) => {
  if (useCookieLoginStore().pendingMFASetup) {
    next();
  } else {
    next({ name: 'login' });
  }
};

export const ifPendingReset = (
  _to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext,
) => {
  if (useCookieLoginStore().pendingResetToken) {
    next();
  } else {
    next({ name: 'login' });
  }
};

/*
 * The referral page signs in with legacy's second factor and runs in bearer
 * mode, which is picked once per page load (boot/authMode). Reaching it
 * inside a cookie-mode page — a link pasted into an open tab only changes the
 * address — reloads the page at that address, so boot picks bearer mode.
 */
export const reloadIntoBearerMode = (
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext,
) => {
  if (!isCookieAuthMode()) {
    next();
    return;
  }
  // replaceState, not location.assign: no second navigation.
  window.history.replaceState(window.history.state, '', to.fullPath);
  reloadPage();
  next(false);
};

// Legacy's second-factor, phone and change-password screens. Go does the
// second factor and passwords in cookie mode, so they are bearer-only.
export const ifBearerMode = (
  _to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext,
) => {
  if (isCookieAuthMode()) {
    next({ name: 'login' });
  } else {
    next();
  }
};

export const validMedicationId = (
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext,
) => {
  // Validate UUID format
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (!uuidRegex.test(to.params.medicationID as string)) {
    next({ name: 'medkit' });
  } else {
    next();
  }
};

export const requireMedKit = (
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext,
) => {
  const authStore = useAuthStore();

  // Check if the route requires MedKit module
  if (to.matched.some((record) => record.meta.requiresMedKit)) {
    if (!authStore.showMedKit) {
      // MedKit module not enabled, redirect to home
      next({ name: 'home' });
      return;
    }
  }

  next();
};
